import React, { useCallback, useEffect, useState } from 'react'
import { FaExternalLinkAlt, FaPaperclip, FaTimes } from 'react-icons/fa'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { DETAIL_SECTIONS, formatClientName, formatFieldLabel, formatFieldValue, groupDocuments } from './formatting'
import { ClientCommsPanel } from './clientCommsPanel'
import styles from './claimDetailDrawer.module.scss'

export interface OmnibusClaimDocument {
    id: number
    doc_key: string
    doc_name: string
    s3_key: string | null
    optional: boolean
    source: string
    url: string | null
}

export interface OmnibusClaimDetailPayload {
    /** Sentinel admins only; `documents` is empty for everyone else. */
    viewer_is_admin?: boolean
    detail: Record<string, any>
    snapshots: {
        claim: Record<string, any> | null
        client: Record<string, any> | null
        reference: Record<string, any> | null
        batch: Record<string, any> | null
        lead: Record<string, any> | null
    }
    documents: OmnibusClaimDocument[]
}

/** Minimum a caller needs to supply to open the drawer. */
export interface ClaimDrawerTarget {
    id: number
    funder_case_ref: string
    funder_client_id: number
    first_name?: string | null
    last_name?: string | null
}

interface ClaimDetailDrawerProps {
    claim: ClaimDrawerTarget | null
    onClose: () => void
}

export const ClaimDetailDrawer = ({ claim, onClose }: ClaimDetailDrawerProps) => {
    const [detail, setDetail] = useState<OmnibusClaimDetailPayload | null>(null)
    const [loading, setLoading] = useState(false)
    const [showRaw, setShowRaw] = useState(false)

    const fetchDetail = useCallback(async (omnibusClaimId: number) => {
        setLoading(true)
        setDetail(null)
        try {
            const response = await api.post('omnibus-claim-detail', {
                omnibus_claim_id: omnibusClaimId,
            })
            if (!response.data?.success) {
                Report.failure(
                    'No captured record',
                    response.data?.message || 'This claim has no captured detail yet.',
                    'Okay',
                )
                return
            }
            setDetail(response.data as OmnibusClaimDetailPayload)
        } catch (err: any) {
            Report.failure(
                'Error',
                err?.response?.data?.message || err?.message || 'Failed to load claim detail.',
                'Okay',
            )
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        setShowRaw(false)
        if (claim) fetchDetail(claim.id)
        else setDetail(null)
    }, [claim, fetchDetail])

    useEffect(() => {
        if (!claim) return undefined
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [claim, onClose])

    if (!claim) return null

    return (
        <div className={styles.drawerOverlay} onClick={onClose}>
            <aside className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawerHeader}>
                    <div>
                        <h3 className={styles.drawerTitle}>{claim.funder_case_ref}</h3>
                        <span className={styles.drawerMeta}>
                            {formatClientName(claim.first_name, claim.last_name, String(claim.funder_client_id))}
                        </span>
                    </div>
                    <button
                        type="button"
                        className={styles.drawerClose}
                        onClick={onClose}
                        aria-label="Close claim detail"
                    >
                        <FaTimes />
                    </button>
                </div>

                {loading && <div className={styles.emptyState}>Loading claim record…</div>}

                {!loading && !detail && (
                    <div className={styles.drawerBody}>
                        <div className={styles.emptyState}>
                            No captured record for this claim yet. Run the import to capture it.
                        </div>
                        <ClientCommsPanel omnibusClaimId={claim.id} />
                    </div>
                )}

                {!loading && detail && (
                    <div className={styles.drawerBody}>
                        {DETAIL_SECTIONS.map((section) => {
                            const rows = section.keys
                                .filter((key) => key in detail.detail)
                                .map((key) => ({ key, value: detail.detail[key] }))
                            if (rows.length === 0) return null
                            return (
                                <section key={section.title} className={styles.detailSection}>
                                    <h4 className={styles.detailSectionTitle}>{section.title}</h4>
                                    <dl className={styles.detailGrid}>
                                        {rows.map(({ key, value }) => (
                                            <div key={key} className={styles.detailField}>
                                                <dt>{formatFieldLabel(key)}</dt>
                                                <dd>{formatFieldValue(value)}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                </section>
                            )
                        })}

                        {/* Document links are admin-only: the omnibus pages are for
                            monitoring, not reviewing paperwork. The backend already
                            omits them for non-admins; this hides the heading too. */}
                        {detail.viewer_is_admin && (
                        <section className={styles.detailSection}>
                            <h4 className={styles.detailSectionTitle}>Documents ({detail.documents.length})</h4>
                            {detail.documents.length === 0 ? (
                                <div className={styles.emptyState}>No documents captured for this claim.</div>
                            ) : (
                                groupDocuments(detail.documents).map((group) => (
                                    <div key={group.title} className={styles.docGroup}>
                                        <h5 className={styles.docGroupTitle}>
                                            {group.title}
                                            <span className={styles.docGroupCount}>{group.docs.length}</span>
                                        </h5>
                                        <ul className={styles.docList}>
                                            {group.docs.map((doc) => (
                                                <li key={doc.id} className={styles.docItem}>
                                                    <span className={styles.docName}>
                                                        <FaPaperclip /> {doc.doc_name}
                                                        {doc.optional && (
                                                            <em className={styles.docOptional}>optional</em>
                                                        )}
                                                    </span>
                                                    {doc.url ? (
                                                        <a
                                                            className={styles.docLink}
                                                            href={doc.url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            Open <FaExternalLinkAlt />
                                                        </a>
                                                    ) : (
                                                        <span className={styles.pendingPill}>Link unavailable</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            )}
                        </section>
                        )}

                        <ClientCommsPanel omnibusClaimId={claim.id} />

                        <section className={styles.detailSection}>
                            <button
                                type="button"
                                className={styles.rawToggle}
                                onClick={() => setShowRaw((current) => !current)}
                            >
                                {showRaw ? 'Hide' : 'Show'} raw source records
                            </button>
                            {showRaw && (
                                <div className={styles.rawSnapshots}>
                                    {(['claim', 'client', 'reference', 'batch', 'lead'] as const).map((key) =>
                                        detail.snapshots[key] ? (
                                            <details key={key} className={styles.rawBlock}>
                                                <summary>{formatFieldLabel(key)} record</summary>
                                                <pre>{JSON.stringify(detail.snapshots[key], null, 2)}</pre>
                                            </details>
                                        ) : null,
                                    )}
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </aside>
        </div>
    )
}
